// removeInsertColumn — VQE-Edit edit-action: drop a column (by 0-based list
// index) from an `INSERT INTO t (cols…) VALUES (…), (…)` statement's explicit
// column list via SR-Edit's `remove-insert-column` op. SR-Edit plans the
// multi-range, all-or-nothing edit (the column-list removal PLUS the matching
// cell in EVERY VALUES row — §2.4 S6); this module only validates the index
// against an optional structure and delegates to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type RemoveInsertColumnOptions = {
  /**
   * Optional structure used for index-bounds + last-column validation. Lets
   * the caller surface a friendlier error than SR-Edit's silent "no edits"
   * response.
   */
  readonly structure?: QueryStructure;
};

export async function removeInsertColumn(
  index: number,
  ctx: CommitEditCtx,
  options: RemoveInsertColumnOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(
      `removeInsertColumn: index must be a non-negative integer (got ${index})`,
    );
  }
  if (options.structure) {
    const cols = options.structure.insertColumns ?? [];
    if (cols.length === 0) {
      throw new Error('removeInsertColumn: statement has no explicit column list');
    }
    // Removing the last remaining column would leave rows with cells but no
    // column list to name them (decision 5) — refuse before commitEdit.
    if (cols.length === 1) {
      throw new Error('removeInsertColumn: at least one column must remain');
    }
    if (index >= cols.length) {
      throw new Error(
        `removeInsertColumn: index ${index} out of range (column list has ${cols.length} column(s))`,
      );
    }
  }
  return commitEdit({ op: 'remove-insert-column', index }, ctx);
}
